'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { NavFilter } from '@/lib/specmap/filters';
import {
  archiveNode,
  buildExportPayload,
  createComment,
  createDependency,
  createScreen,
  createShareLink,
  DEFAULT_SPEC_MAP_ID,
  deleteNode,
  deleteScreen,
  duplicateNode,
  duplicateSpecMap,
  flagNodeForReview,
  getSpecMapState,
  importSpecMapPayload,
  markNotificationsRead,
  subscribeToSpecMap,
  switchVersion,
  updateLane,
  updateNode,
  updateScreen,
  updateSpecMapMetadata,
  uploadSpecMapLogo,
} from '@/lib/specmap/repository';
import {
  CreateDependencyInput,
  CreateScreenInput,
  LanePatch,
  NodePatch,
  SpecMapExport,
  SpecMapMetadataPatch,
  SpecMapState,
} from '@/lib/specmap/types';
import SpecMapCanvas from './SpecMapCanvas';
import SpecMapDocumentation from './SpecMapDocumentation';
import SpecMapHeader from './SpecMapHeader';
import SpecMapInspector from './SpecMapInspector';
import SpecMapMetrics from './SpecMapMetrics';
import SpecMapProgressReport from './SpecMapProgressReport';
import SpecMapSidebar from './SpecMapSidebar';

interface SpecMapShellProps {
  specMapId?: string;
}

export default function SpecMapShell({ specMapId = DEFAULT_SPEC_MAP_ID }: SpecMapShellProps) {
  const [state, setState] = useState<SpecMapState | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeLanes, setActiveLanes] = useState<string[]>([]);
  const [activeNavItem, setActiveNavItem] = useState<NavFilter>('nav-overview');
  const [zoomLevel, setZoomLevel] = useState(100);

  const applyState = useCallback((nextState: SpecMapState) => {
    setState(nextState);
    setActiveLanes((current) => {
      const laneIds = nextState.lanes.map((lane) => lane.id);
      const stillValid = current.filter((lane) => laneIds.includes(lane));
      return stillValid.length > 0 ? stillValid : laneIds;
    });
    setSelectedNodeId((current) => {
      if (nextState.nodes.some((node) => node.id === current && !node.archivedAt)) {
        return current;
      }

      return (
        nextState.nodes.find((node) => node.id === 'node-user-02' && !node.archivedAt)?.id ??
        nextState.nodes.find((node) => !node.archivedAt)?.id ??
        ''
      );
    });
  }, []);

  const loadState = useCallback(async () => {
    try {
      const nextState = await getSpecMapState(specMapId);
      applyState(nextState);
    } catch (error) {
      toast.error('Could not load spec map', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  }, [applyState, specMapId]);

  useEffect(() => {
    void loadState();
  }, [loadState]);

  useEffect(() => {
    const unsubscribe = subscribeToSpecMap(
      specMapId,
      () => void loadState(),
      (collaborators) => {
        setState((current) => (current ? { ...current, collaborators } : current));
      }
    );

    return unsubscribe;
  }, [loadState, specMapId]);

  const selectedNode = useMemo(
    () => state?.nodes.find((node) => node.id === selectedNodeId),
    [selectedNodeId, state]
  );
  const activeWorkspace = activeNavItem === 'nav-progress' || activeNavItem === 'nav-docs';

  const handleNodeSelect = useCallback((id: string) => {
    setSelectedNodeId(id);
    setInspectorOpen(true);
  }, []);

  const handleToggleLane = useCallback((lane: string) => {
    setActiveLanes((prev) =>
      prev.includes(lane) ? prev.filter((item) => item !== lane) : [...prev, lane]
    );
  }, []);

  const runMutation = useCallback(
    async (mutation: Promise<SpecMapState>, errorMessage: string) => {
      try {
        const nextState = await mutation;
        applyState(nextState);
      } catch (error) {
        toast.error(errorMessage, {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    },
    [applyState]
  );

  const handleUpdateNode = useCallback(
    async (nodeId: string, patch: NodePatch) => {
      await runMutation(updateNode(specMapId, nodeId, patch), 'Could not update node');
    },
    [runMutation, specMapId]
  );

  const handleUpdateSpecMap = useCallback(
    async (patch: SpecMapMetadataPatch) => {
      await runMutation(updateSpecMapMetadata(specMapId, patch), 'Could not update map details');
    },
    [runMutation, specMapId]
  );

  const handleUploadLogo = useCallback(
    async (file: File) => {
      await runMutation(uploadSpecMapLogo(specMapId, file), 'Could not upload logo');
    },
    [runMutation, specMapId]
  );

  const handleClearLogo = useCallback(async () => {
    await runMutation(
      updateSpecMapMetadata(specMapId, { logoUrl: null, logoAlt: '' }),
      'Could not clear logo'
    );
  }, [runMutation, specMapId]);

  const handleUpdateLane = useCallback(
    async (laneId: string, patch: LanePatch) => {
      await runMutation(updateLane(specMapId, laneId, patch), 'Could not update lane');
    },
    [runMutation, specMapId]
  );

  const handleNodePositionChange = useCallback(
    async (nodeId: string, position: { x: number; y: number }) => {
      setState((current) =>
        current
          ? {
              ...current,
              nodes: current.nodes.map((node) =>
                node.id === nodeId
                  ? { ...node, positionX: position.x, positionY: position.y }
                  : node
              ),
            }
          : current
      );

      try {
        const nextState = await updateNode(specMapId, nodeId, {
          positionX: position.x,
          positionY: position.y,
        });
        applyState(nextState);
      } catch (error) {
        toast.error('Could not save node position', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
        void loadState();
      }
    },
    [applyState, loadState, specMapId]
  );

  const handleShare = useCallback(async () => {
    try {
      const result = await createShareLink(specMapId);
      applyState(result.state);
      await navigator.clipboard.writeText(result.shareLink.url);
      toast.success('Share link copied to clipboard', {
        description: 'Anyone with the link can view this spec map.',
      });
    } catch (error) {
      toast.error('Could not create share link', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, [applyState, specMapId]);

  const handleExportJson = useCallback(() => {
    if (!state) {
      return;
    }

    const payload = buildExportPayload(state);
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${state.specMap.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-specmap.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success('Spec map exported as JSON');
  }, [state]);

  const handleImportJson = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async () => {
      const file = input.files?.[0];

      if (!file) {
        return;
      }

      try {
        const payload = JSON.parse(await file.text()) as SpecMapExport;
        const nextState = await importSpecMapPayload(payload);
        applyState(nextState);
        toast.success('Spec map JSON imported');
      } catch (error) {
        toast.error('Could not import JSON', {
          description: error instanceof Error ? error.message : 'Invalid JSON file',
        });
      }
    };
    input.click();
  }, [applyState]);

  const handleMenuAction = useCallback(
    (action: 'duplicate-map' | 'archive-map' | 'import-json' | 'export-json') => {
      if (action === 'export-json') {
        handleExportJson();
        return;
      }

      if (action === 'import-json') {
        handleImportJson();
        return;
      }

      if (action === 'archive-map') {
        void runMutation(
          updateSpecMapMetadata(specMapId, { status: 'Deprecated' }),
          'Could not archive map'
        );
        return;
      }

      void runMutation(duplicateSpecMap(specMapId), 'Could not duplicate map');
    },
    [handleExportJson, handleImportJson, runMutation, specMapId]
  );

  const handleDeleteNode = useCallback(
    async (nodeId: string) => {
      await runMutation(deleteNode(specMapId, nodeId), 'Could not delete node');
      setInspectorOpen(false);
    },
    [runMutation, specMapId]
  );

  if (loading || !state) {
    return (
      <div data-component-id="src/app/components/SpecMapShell.tsx:301:6" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="301" data-component-end-line="320" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20h-screen%20w-full%20overflow-hidden%20bg-background%22%7D" className="flex flex-col h-screen w-full overflow-hidden bg-background">
        <Toaster data-component-id="src/app/components/SpecMapShell.tsx:302:8" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="302" data-component-end-line="302" data-component-file="SpecMapShell.tsx" data-component-name="Toaster" data-component-content="%7B%22elementName%22%3A%22Toaster%22%7D" position="bottom-right" />
        <div data-component-id="src/app/components/SpecMapShell.tsx:303:8" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="303" data-component-end-line="307" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22header-blur%20border-b%20border-border%20h-14%20flex%20items-center%20px-4%20gap-3%22%7D" className="header-blur border-b border-border h-14 flex items-center px-4 gap-3">
          <div data-component-id="src/app/components/SpecMapShell.tsx:304:10" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="304" data-component-end-line="304" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-28%20h-4%20rounded%20bg-muted%20animate-pulse%22%7D" className="w-28 h-4 rounded bg-muted animate-pulse" />
          <div data-component-id="src/app/components/SpecMapShell.tsx:305:10" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="305" data-component-end-line="305" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1" />
          <div data-component-id="src/app/components/SpecMapShell.tsx:306:10" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="306" data-component-end-line="306" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-48%20h-8%20rounded-lg%20bg-muted%20animate-pulse%20hidden%20md%3Ablock%22%7D" className="w-48 h-8 rounded-lg bg-muted animate-pulse hidden md:block" />
        </div>
        <div data-component-id="src/app/components/SpecMapShell.tsx:308:8" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="308" data-component-end-line="319" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-1%20overflow-hidden%22%7D" className="flex flex-1 overflow-hidden">
          <div data-component-id="src/app/components/SpecMapShell.tsx:309:10" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="309" data-component-end-line="313" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-60%20sidebar-bg%20border-r%20border-border%20p-3%20space-y-3%22%7D" className="w-60 sidebar-bg border-r border-border p-3 space-y-3">
            {Array.from({ length: 8 }).map((_, index) => (
              <div data-component-id="src/app/components/SpecMapShell.tsx:311:14" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="311" data-component-end-line="311" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20rounded-lg%20bg-muted%2F50%20animate-pulse%22%7D" key={index} className="h-8 rounded-lg bg-muted/50 animate-pulse" />
            ))}
          </div>
          <div data-component-id="src/app/components/SpecMapShell.tsx:314:10" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="314" data-component-end-line="318" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20p-6%20space-y-4%22%7D" className="flex-1 p-6 space-y-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div data-component-id="src/app/components/SpecMapShell.tsx:316:14" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="316" data-component-end-line="316" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-40%20rounded-xl%20bg-card%20border%20border-border%20animate-pulse%22%7D" key={index} className="h-40 rounded-xl bg-card border border-border animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/components/SpecMapShell.tsx:325:4" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="325" data-component-end-line="446" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20h-screen%20w-full%20overflow-hidden%20bg-background%22%7D" className="flex flex-col h-screen w-full overflow-hidden bg-background">
      <Toaster data-component-id="src/app/components/SpecMapShell.tsx:326:6" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="326" data-component-end-line="336" data-component-file="SpecMapShell.tsx" data-component-name="Toaster" data-component-content="%7B%22elementName%22%3A%22Toaster%22%7D"
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#242019',
            border: '1px solid #3A3430',
            color: '#F5F0EB',
            fontSize: '13px',
          },
        }}
      />

      <SpecMapHeader data-component-id="src/app/components/SpecMapShell.tsx:338:6" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="338" data-component-end-line="364" data-component-file="SpecMapShell.tsx" data-component-name="SpecMapHeader" data-component-content="%7B%22elementName%22%3A%22SpecMapHeader%22%7D"
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        specMap={state.specMap}
        versions={state.versions}
        activeVersionId={state.specMap.activeVersionId}
        lanes={state.lanes}
        collaborators={state.collaborators}
        notifications={state.notifications}
        source={state.source}
        onVersionChange={(versionId) =>
          runMutation(switchVersion(specMapId, versionId), 'Could not switch version')
        }
        onUpdateSpecMap={handleUpdateSpecMap}
        onUploadLogo={handleUploadLogo}
        onClearLogo={handleClearLogo}
        onUpdateLane={handleUpdateLane}
        onShare={handleShare}
        onExportJson={handleExportJson}
        onOpenPreview={() => window.open(`/spec-maps/${state.specMap.id}/preview`, '_blank')}
        onMarkNotificationsRead={() =>
          runMutation(markNotificationsRead(specMapId), 'Could not update notifications')
        }
        onMenuAction={handleMenuAction}
      />

      <div data-component-id="src/app/components/SpecMapShell.tsx:366:6" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="366" data-component-end-line="445" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-1%20overflow-hidden%22%7D" className="flex flex-1 overflow-hidden">
        <SpecMapSidebar data-component-id="src/app/components/SpecMapShell.tsx:367:8" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="367" data-component-end-line="378" data-component-file="SpecMapShell.tsx" data-component-name="SpecMapSidebar" data-component-content="%7B%22elementName%22%3A%22SpecMapSidebar%22%7D"
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          activeLanes={activeLanes}
          onToggleLane={handleToggleLane}
          selectedNodeId={selectedNodeId}
          onNodeSelect={handleNodeSelect}
          nodes={state.nodes}
          lanes={state.lanes}
          activeNavItem={activeNavItem}
          onNavChange={setActiveNavItem}
        />

        <div data-component-id="src/app/components/SpecMapShell.tsx:380:8" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="380" data-component-end-line="444" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20flex-1%20overflow-hidden%22%7D" className="flex flex-col flex-1 overflow-hidden">
          <SpecMapMetrics data-component-id="src/app/components/SpecMapShell.tsx:381:10" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="381" data-component-end-line="381" data-component-file="SpecMapShell.tsx" data-component-name="SpecMapMetrics" data-component-content="%7B%22elementName%22%3A%22SpecMapMetrics%22%7D" metrics={state.metrics} lastSavedLabel={state.lastSavedLabel} />

          {activeNavItem === 'nav-progress' && <SpecMapProgressReport data-component-id="src/app/components/SpecMapShell.tsx:383:47" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="383" data-component-end-line="383" data-component-file="SpecMapShell.tsx" data-component-name="SpecMapProgressReport" data-component-content="%7B%22elementName%22%3A%22SpecMapProgressReport%22%7D" state={state} />}
          {activeNavItem === 'nav-docs' && <SpecMapDocumentation data-component-id="src/app/components/SpecMapShell.tsx:384:43" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="384" data-component-end-line="384" data-component-file="SpecMapShell.tsx" data-component-name="SpecMapDocumentation" data-component-content="%7B%22elementName%22%3A%22SpecMapDocumentation%22%7D" state={state} />}

          {!activeWorkspace && (
            <div data-component-id="src/app/components/SpecMapShell.tsx:387:12" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="387" data-component-end-line="442" data-component-file="SpecMapShell.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-1%20overflow-hidden%22%7D" className="flex flex-1 overflow-hidden">
              <SpecMapCanvas data-component-id="src/app/components/SpecMapShell.tsx:388:14" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="388" data-component-end-line="399" data-component-file="SpecMapShell.tsx" data-component-name="SpecMapCanvas" data-component-content="%7B%22elementName%22%3A%22SpecMapCanvas%22%7D"
                selectedNodeId={selectedNodeId}
                onNodeSelect={handleNodeSelect}
                searchQuery={searchQuery}
                activeLanes={activeLanes}
                zoomLevel={zoomLevel}
                nodes={state.nodes}
                lanes={state.lanes}
                specMap={state.specMap}
                activeNavItem={activeNavItem}
                onNodePositionChange={handleNodePositionChange}
              />

              {inspectorOpen && selectedNode && (
                <SpecMapInspector data-component-id="src/app/components/SpecMapShell.tsx:402:16" data-component-path="src/app/components/SpecMapShell.tsx" data-component-line="402" data-component-end-line="440" data-component-file="SpecMapShell.tsx" data-component-name="SpecMapInspector" data-component-content="%7B%22elementName%22%3A%22SpecMapInspector%22%7D"
                  node={selectedNode}
                  nodes={state.nodes}
                  lanes={state.lanes}
                  comments={state.comments}
                  activityData={state.activitySeries[selectedNode.id] ?? []}
                  onClose={() => setInspectorOpen(false)}
                  onNodeSelect={handleNodeSelect}
                  onUpdateNode={handleUpdateNode}
                  onCreateComment={(nodeId, message) =>
                    runMutation(createComment(specMapId, nodeId, message), 'Could not add comment')
                  }
                  onCreateDependency={(input: CreateDependencyInput) =>
                    runMutation(createDependency(specMapId, input), 'Could not link dependency')
                  }
                  onCreateScreen={(nodeId, input: CreateScreenInput) =>
                    runMutation(createScreen(specMapId, nodeId, input), 'Could not add screen')
                  }
                  onUpdateScreen={(nodeId, screenId, input) =>
                    runMutation(
                      updateScreen(specMapId, nodeId, screenId, input),
                      'Could not update screen'
                    )
                  }
                  onDeleteScreen={(nodeId, screenId) =>
                    runMutation(deleteScreen(specMapId, nodeId, screenId), 'Could not delete screen')
                  }
                  onFlagNode={(nodeId) =>
                    runMutation(flagNodeForReview(specMapId, nodeId), 'Could not flag node')
                  }
                  onDuplicateNode={(nodeId) =>
                    runMutation(duplicateNode(specMapId, nodeId), 'Could not duplicate node')
                  }
                  onArchiveNode={(nodeId) => {
                    setInspectorOpen(false);
                    return runMutation(archiveNode(specMapId, nodeId), 'Could not archive node');
                  }}
                  onDeleteNode={handleDeleteNode}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
